import React, { useState, useMemo } from "react";
import PageLoader from "../components/ui/PageLoader";
import Modal from "../components/ui/Modal";
import Button from "../components/ui/Button";
import { useGetOrdersAdmin, useProcessRefund } from "apis/order";
import Table from "components/ui/Table";

const Order = () => {
  const [viewModal, setViewModal] = useState(false);
  const [refundModal, setRefundModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");

  // Server-side pagination
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const { data: ordersData, isPending } = useGetOrdersAdmin({
    page,
    limit: pageSize,
  });
  const orders = ordersData?.data || [];
  const pagination = ordersData?.pagination || {
    page: 1,
    limit: pageSize,
    total: 0,
    totalPages: 1,
  };

  const processRefund = useProcessRefund();

  const formatCurrency = (amount) =>
    `$${(amount || 0).toLocaleString("en-AU", { minimumFractionDigits: 2 })}`;

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString("en-AU", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "-";

  // Charge date: prefer paidAt, fall back to createdAt (backfilled rows backdate
  // createdAt to the charge date too).
  const chargeDate = (order) => order?.paidAt || order?.createdAt;

  const getStatusBadge = (status) => {
    const styles = {
      paid: "bg-green-100 text-green-800",
      refunded: "bg-blue-100 text-blue-800",
      failed: "bg-red-100 text-red-800",
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
          styles[status] || "bg-lightgray text-darkgray"
        }`}
      >
        {status || "-"}
      </span>
    );
  };

  const getCategoryBadge = (purchaseType) => {
    const styles = {
      subscription: "bg-blue-100 text-blue-800",
      campaign: "bg-green-100 text-green-800",
      priceProcess: "bg-purple-100 text-purple-800",
      marketing: "bg-lightgray text-darkgray",
      photoCredits: "bg-violet-100 text-violet-800",
      editorReview: "bg-rose-100 text-rose-800",
    };
    if (!purchaseType) return <span className="text-darkgray">-</span>;
    const labels = {
      photoCredits: "photo credits",
      editorReview: "editor review",
    };
    return (
      <span
        className={`px-2 py-0.5 rounded text-xs capitalize ${
          styles[purchaseType] || "bg-lightgray text-darkgray"
        }`}
      >
        {labels[purchaseType] || purchaseType}
      </span>
    );
  };

  const handleProcessRefund = async () => {
    if (!selectedOrder) return;
    await processRefund.mutateAsync({
      orderId: selectedOrder._id,
      refundAmount: parseFloat(refundAmount),
      refundReason: refundReason.trim() || undefined,
    });
    setRefundModal(false);
    setSelectedOrder(null);
    setRefundReason("");
  };

  const openRefund = (order) => {
    setSelectedOrder(order);
    setRefundAmount(order.totalAmount?.toString() || "");
    setRefundReason("");
    setRefundModal(true);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "chargedDate",
        header: "Charged Date",
        cell: (row) => formatDate(chargeDate(row)),
        isFilter: false,
      },
      {
        accessorKey: "userId.name",
        header: "Agent",
        cell: (row) => row.userId?.name || "-",
        isFilter: true,
      },
      {
        accessorKey: "productName",
        header: "Product Name",
        isFilter: true,
      },
      {
        accessorKey: "address",
        header: "Property",
        cell: (row) => row.address || "-",
        isFilter: true,
        maxWidth: "240px",
      },
      {
        accessorKey: "purchaseType",
        header: "Category",
        cell: (row) => getCategoryBadge(row.purchaseType),
        isFilter: true,
      },
      {
        accessorKey: "totalAmount",
        header: "Amount",
        cell: (row) => formatCurrency(row.totalAmount),
        isFilter: false,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (row) => getStatusBadge(row.status),
        isFilter: true,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => {
          const order = row;
          return (
            <div className="flex items-center gap-2">
              {order.status === "paid" && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openRefund(order);
                  }}
                  className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                >
                  Refund
                </button>
              )}
              {order.status === "refunded" && (
                <span className="text-xs text-darkgray">
                  Refunded {formatDate(order.refundedAt)}
                </span>
              )}
            </div>
          );
        },
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
      },
    ],
    [],
  );

  if (isPending) return <PageLoader />;

  const handleViewRowClick = (row) => {
    setSelectedOrder(row);
    setViewModal(true);
  };

  return (
    <>
      {/* View Modal */}
      <Modal
        isOpen={viewModal}
        onClose={() => setViewModal(false)}
        title="Order Details"
      >
        {selectedOrder && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-darkgray">Agent</p>
                <p className="font-medium">
                  {selectedOrder.userId?.name || "-"}
                </p>
              </div>
              <div>
                <p className="text-darkgray">Status</p>
                {getStatusBadge(selectedOrder.status)}
              </div>
              <div>
                <p className="text-darkgray">Product Name</p>
                <p className="font-medium">{selectedOrder.productName}</p>
              </div>
              {selectedOrder.address && (
                <div>
                  <p className="text-darkgray">Property</p>
                  <p className="font-medium">{selectedOrder.address}</p>
                </div>
              )}
              <div>
                <p className="text-darkgray">Category</p>
                {getCategoryBadge(selectedOrder.purchaseType)}
              </div>
              <div>
                <p className="text-darkgray">Amount</p>
                <p className="font-semibold">
                  {formatCurrency(selectedOrder.totalAmount)}
                </p>
              </div>
              <div>
                <p className="text-darkgray">Charged Date</p>
                <p>{formatDate(chargeDate(selectedOrder))}</p>
              </div>
              {selectedOrder.invoiceNumber && (
                <div>
                  <p className="text-darkgray">Invoice #</p>
                  <p>{selectedOrder.invoiceNumber}</p>
                </div>
              )}
              {selectedOrder.billingPeriod?.start && (
                <div>
                  <p className="text-darkgray">Billing Period</p>
                  <p>
                    {formatDate(selectedOrder.billingPeriod.start)} →{" "}
                    {formatDate(selectedOrder.billingPeriod.end)}
                  </p>
                </div>
              )}
            </div>

            {selectedOrder.status === "refunded" && (
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <p className="text-xs text-darkgray">Refunded</p>
                <p className="text-sm">
                  {formatDate(selectedOrder.refundedAt)}
                  {selectedOrder.refundAmount != null
                    ? ` · ${formatCurrency(selectedOrder.refundAmount)}`
                    : ""}
                  {selectedOrder.stripeRefundId
                    ? ` · ${selectedOrder.stripeRefundId}`
                    : ""}
                </p>
                {selectedOrder.refundReason && (
                  <p className="text-sm mt-1">
                    <span className="text-darkgray">Reason: </span>
                    {selectedOrder.refundReason}
                  </p>
                )}
              </div>
            )}

            {selectedOrder.receiptUrl && (
              <a
                href={selectedOrder.receiptUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline text-sm"
              >
                View Receipt →
              </a>
            )}

            {selectedOrder.status === "paid" && (
              <div className="pt-2">
                <Button
                  className="black-button"
                  onClick={() => {
                    setViewModal(false);
                    openRefund(selectedOrder);
                  }}
                >
                  Refund this order
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Refund Modal */}
      <Modal
        isOpen={refundModal}
        onClose={() => setRefundModal(false)}
        title="Process Refund"
      >
        {selectedOrder && (
          <div className="space-y-4">
            <div className="bg-lightgray rounded p-3">
              <p className="font-medium">{selectedOrder.productName}</p>
              <p className="text-sm text-darkgray">
                {selectedOrder.userId?.name || "-"}
              </p>
            </div>
            <div>
              <label className="form-label">
                Refund Amount
              </label>
              <input
                type="number"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                max={selectedOrder.totalAmount}
                min="0"
                step="0.01"
                className="w-full border border-mediumgray rounded px-3 py-2"
              />
              <p className="text-xs text-darkgray mt-1">
                Max: {formatCurrency(selectedOrder.totalAmount)}
              </p>
            </div>
            <div>
              <label className="form-label">
                Reason <span className="text-darkgray">(optional)</span>
              </label>
              <textarea
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                className="w-full form-textarea border border-mediumgray"
                rows={3}
                placeholder="Why is this being refunded?"
              />
            </div>
            <div className="flex gap-3">
              <Button
                className="flex-1 gray-button"
                onClick={() => setRefundModal(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 black-button"
                onClick={handleProcessRefund}
                loading={processRefund.isPending}
                disabled={
                  !refundAmount ||
                  parseFloat(refundAmount) <= 0 ||
                  parseFloat(refundAmount) > (selectedOrder.totalAmount || 0)
                }
              >
                Process Refund
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Table */}
      <div className="container mx-auto">
        <Table
          title="Orders"
          columns={columns}
          data={orders}
          pageSize={pageSize}
          onRowClick={handleViewRowClick}
          serverPagination={{
            page: pagination.page,
            totalPages: pagination.totalPages,
            total: pagination.total,
            limit: pagination.limit,
          }}
          onPageChange={(p) => setPage(p)}
        />
      </div>
    </>
  );
};

export default Order;
